import type {OwnerEquipmentReport} from '../app/data/ownerEquipmentReportsData.js';
import {patchFuelingField} from '../lib/equipmentFuelingsApi.js';
import {updateOwnerFuelSupply} from '../lib/ownerReportsApi.js';
import type {SupabaseClient} from '../lib/supabaseTypes.js';

export type OwnerReportDraft = Record<string, string>;
export type ReportField = {key: string; label: string; type: 'date' | 'number' | 'text' | 'notes'; required?: boolean};
export function ownerReportFields(report: OwnerEquipmentReport, role: string): ReportField[] {
  const fields: ReportField[] = [
    {key: 'date', label: 'Date', type: 'date', required: true},
    {key: 'gallons', label: 'Fuel (gal)', type: 'number', required: report.kind === 'supply'},
  ];
  if (report.kind === 'fueling')
    return [
      ...fields,
      {
        key: report.unit === 'km' ? 'km_reading' : 'hours_reading',
        label: report.unit === 'km' ? 'Reading (km)' : 'Reading (hr)',
        type: 'number',
      },
      ...(role === 'light' ? [] : [{key: 'def_gallons', label: 'DEF (gal)', type: 'number' as const}]),
      {key: 'comments', label: 'Notes', type: 'notes'},
    ];
  return [
    ...fields,
    {key: 'fuel_type', label: 'Fuel Type', type: 'text'},
    {key: 'destination', label: 'Destination', type: 'text', required: true},
    ...(role === 'admin'
      ? [
          {key: 'supplier', label: 'Supplier', type: 'text' as const},
          {key: 'cost_per_gal', label: 'Cost Per Gallon ($)', type: 'number' as const},
          {key: 'total_cost', label: 'Total Cost ($)', type: 'number' as const},
          {key: 'team_member', label: 'Submitted By', type: 'text' as const},
        ]
      : []),
    {key: 'notes', label: 'Notes', type: 'notes'},
  ];
}
export function ownerReportDraft(report: OwnerEquipmentReport, fields: ReportField[]): OwnerReportDraft {
  return Object.fromEntries(
    fields.map((field) => [field.key, report.row[field.key] == null ? '' : String(report.row[field.key])]),
  );
}
export function validateOwnerReportDraft(draft: OwnerReportDraft, fields: ReportField[]) {
  const errors: Record<string, string> = {};
  for (const field of fields) {
    const value = draft[field.key] ?? '';
    if (field.required && !value.trim()) errors[field.key] = 'Enter ' + field.label.toLowerCase() + '.';
    else if (field.type === 'date' && !/^\d{4}-\d{2}-\d{2}$/.test(value)) errors[field.key] = 'Choose a date.';
    else if (field.type === 'number' && value.trim()) {
      const n = Number(value);
      if (
        !Number.isFinite(n) ||
        (!['cost_per_gal', 'total_cost'].includes(field.key) && (n < 0 || (field.required && n === 0)))
      ) {
        errors[field.key] = field.required ? 'Enter a number greater than zero.' : 'Enter a valid number.';
      }
    }
  }
  return errors;
}

// Fueling uses existing single-field audited RPCs. Keep a baseline after EACH
// durable field so a later refusal is reported as partial and retry only sends
// the fields still needing save. Never claim this multi-call edit is atomic.
export function createOwnerReportWriter(sb: SupabaseClient, report: OwnerEquipmentReport, fields: ReportField[]) {
  const persisted = ownerReportDraft(report, fields);
  return async (draft: OwnerReportDraft) => {
    const changed = fields.filter((field) => draft[field.key] !== persisted[field.key]);
    if (!changed.length) return;
    // A legacy empty fueling field must not block an unrelated note correction.
    // Supply's atomic patch validates the complete required record server-side.
    const errors = validateOwnerReportDraft(draft, report.kind === 'fueling' ? changed : fields);
    if (Object.keys(errors).length) throw new Error('Fix the marked fields before leaving this report.');
    const value = (field: ReportField) =>
      field.type === 'number' ? (draft[field.key].trim() === '' ? null : Number(draft[field.key])) : draft[field.key];
    if (report.kind === 'supply') {
      await updateOwnerFuelSupply(
        sb,
        report.row.id,
        Object.fromEntries(changed.map((field) => [field.key, value(field)])),
      );
      for (const field of changed) persisted[field.key] = draft[field.key];
      return;
    }
    let saved = 0;
    for (const field of changed) {
      const result = await patchFuelingField(
        sb,
        {equipmentId: report.row.equipment_id, fuelingId: report.row.id},
        field.key,
        value(field),
      );
      if (!result.ok) throw new Error((saved ? 'Some changes saved. ' : '') + result.error);
      persisted[field.key] = draft[field.key];
      saved += 1;
    }
  };
}
